import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { VehicleDriving } from "../../models/vehicle/vehiclePosition";
import { WorkShiftDto } from "../../models/workShift/workShift";
import { WorkShiftTimeSlotItem } from "../../models/workShitTimeSlot/workShiftTimeSlotItem";
import { VehicleTripSegment } from "../../models/vehicle/vehicleTripSegment";
import { VehicleData } from "../../models/monitorView/monitorViewTypes";
import { AppDispatch } from "../../framework/customStore";
import {
    setSelectedVehicleId,
    setSidebarTitle,
    toggleVehicleRouteView,
    fetchVehicleTripSegments,
} from "../../store/monitorViewSlice";
import { handleApiError } from "../../models/store/storeEffects";

export const TabsEnum = Object.freeze({
    VEHICLES: 0,
    EMPLOYEES: 1,
});

export const getShiftAndSlotDuration = (workShifts: WorkShiftDto[], id: string): { timeSlotDuration: string; shiftDuration: string; effectiveDate: string } => {
    const workShiftAndTimeSlot = workShifts
        .flatMap(shift => shift.workShiftTimeSlots.map(timeSlot => ({ workShift: shift, timeSlot })))
        .find(({ workShift, timeSlot }) => timeSlot.employeeId === id && workShift.endTime === null);

    let timeSlotDuration = "";
    if (workShiftAndTimeSlot && workShiftAndTimeSlot.timeSlot) {
        const totalMinutes = new WorkShiftTimeSlotItem(workShiftAndTimeSlot.timeSlot).getDurationMinutes();
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        timeSlotDuration = hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;
    }

    let shiftDuration = "";
    let effectiveDate = "";
    if (workShiftAndTimeSlot && workShiftAndTimeSlot.workShift) {
        const startTime = new Date(workShiftAndTimeSlot.workShift.startTime);
        const now = new Date();
        const diff = now.getTime() - startTime.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        shiftDuration = `${startTime.getDate()}.${startTime.getMonth() + 1} ${startTime.getHours()}:${startTime.getMinutes()} (${hours} h ${minutes} min)`;
        effectiveDate = workShiftAndTimeSlot.workShift.effectiveDate ? new Date(workShiftAndTimeSlot.workShift.effectiveDate).toISOString().split('T')[0] : "";
    }

    return { timeSlotDuration, shiftDuration, effectiveDate };
};

export const getVehicleStatusText = (status: VehicleDriving | string) => {
    const { t } = useTranslation();

    switch (status) {
        case "All":
            return t("monitorView.vehicleDriving.all");
        case VehicleDriving.Inactive:
            return t("monitorView.vehicleDriving.inactive");
        case VehicleDriving.Stopped:
            return t("monitorView.vehicleDriving.stopped");
        case VehicleDriving.Moving:
            return t("monitorView.vehicleDriving.moving");
        case VehicleDriving.Driving:
            return t("monitorView.vehicleDriving.driving");
        default:
            return t("monitorView.vehicleDriving.unknown");
    }
};

export const tripSegmentParams = (vehicleId: string, date: Date) => {
    const startDate = dayjs(date).startOf("day").toDate();
    const endDate = dayjs(startDate).add(1, "day").toDate();
    return {
        vehicleId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
    };
};

export const transformVehicleTripSegments = (segments: VehicleTripSegment[]): VehicleData[] => {
    return segments.flatMap((segment) =>
        segment.positions.map((position) => ({
            brand: segment.brand,
            vehicleId: position.vehicleId,
            registerNumber: segment.registerNumber,
            coords: { lat: position.latitude, lng: position.longitude },
            latitude: position.latitude,
            longitude: position.longitude,
            timestamp: position.timestamp,
            speed: position.speed,
            driving: position.driving,
            employeeName: position?.employeeWorkTimeTypes?.[0]?.employeeName || "",
            workTimeTypeName: position?.employeeWorkTimeTypes?.[0]?.workTimeTypeName || "",
            workTimeTypeType: position?.employeeWorkTimeTypes?.[0]?.workTimeTypeType || null,
            carId: position.carId,
            codriverGmt: position.codriverGmt,
            codriverId: position.codriverId,
            driverGmt: position.driverGmt,
            driverId: position.driverId,
            drivingGmt: position.drivingGmt,
            state: segment.state
        }))
    );
};

export const handleVehicleSelect = (
    vehicleId: string,
    vehicles: any[],
    tripStartDate: Date,
    isVehicleRouteView: boolean,
    dispatch: AppDispatch,
    t: (key: string, options?: any) => string
) => {
    if (vehicleId) {
        dispatch(setSelectedVehicleId(vehicleId));
        const vehicle = vehicles?.find((v) => v.id === vehicleId);
        if (vehicle) {
            dispatch(setSidebarTitle(`${vehicle.registerNumber} - ${vehicle.brand}`));
        }
        if (!isVehicleRouteView) {
            dispatch(toggleVehicleRouteView());
        }
        try {
            dispatch(
                fetchVehicleTripSegments(
                    tripSegmentParams(vehicleId, tripStartDate)
                ) as any
            );
        } catch (e) {
            handleApiError(
                t("monitorView.fetchingTripsFailed", { reason: e }),
                dispatch
            );
        }
    }
};